import * as React from 'react';
import { observer, Observer } from 'mobx-react-lite';
import { Modal, Form, Button, Radio, Select, Input } from 'antd';
import { Rule } from 'antd/es/form';
import { TrainingProjectDashboardStore } from '../../stores';
import { CreateEditTrainingProjectExampleFormValues } from '../../types';
import {
    trainingProjectExampleFormFields,
    trainingProjectExampleSourceTypeValues,
    trainingProjectExampleOutputTypeValues
} from '../../misc/constants';
import { TrainingProjectJsonEditor } from '..';

interface Props {
    store: TrainingProjectDashboardStore;
}

const CreateEditTrainingProjectExampleModal: React.FC<Props> = ({ store }) => {
    const [form] = Form.useForm<CreateEditTrainingProjectExampleFormValues>();
    const [textSourceValue, setTextSourceValue] = React.useState<string | undefined>(
        store.editableExample?.sourceType === trainingProjectExampleSourceTypeValues.Text
            ? store.editableExample?.sourceValue
            : undefined
    );
    const [packageSourceValue, setPackageSourceValue] = React.useState<string | undefined>(
        store.editableExample?.sourceType === trainingProjectExampleSourceTypeValues.PackageId
            ? store.editableExample?.sourceValue
            : undefined
    );
    const [documentNameValue, setDocumentNameValue] = React.useState<string | undefined>(
        store.editableExample?.documentName
    );

    const onFinish = async (formValues: CreateEditTrainingProjectExampleFormValues) => {
        const trimmedFormValues = {
            ...formValues,
            documentName: formValues.documentName?.trim() || undefined
        };

        const completed = store.editableExample
            ? await store.editableExample.update(trimmedFormValues)
            : await store.createExample(trimmedFormValues);

        if (completed) {
            onClose();
        }
    };

    const onClose = () => {
        store.setCreateEditExampleDialogVisible(false);
        store.editableExample?.setEditable(false);
    };

    const onValuesChange = (
        changedValues: Partial<CreateEditTrainingProjectExampleFormValues>,
        allValues: CreateEditTrainingProjectExampleFormValues
    ) => {
        if (changedValues[trainingProjectExampleFormFields.sourceType] !== undefined) {
            const newSourceType = changedValues[trainingProjectExampleFormFields.sourceType];
            const currentSourceValue = allValues[trainingProjectExampleFormFields.sourceValue];
            const currentDocumentName = allValues[trainingProjectExampleFormFields.documentName];

            let restoredValue: string | undefined;
            let restoredDocumentName: string | undefined;

            if (newSourceType === trainingProjectExampleSourceTypeValues.Text) {
                setPackageSourceValue(currentSourceValue);
                restoredValue = textSourceValue;
                restoredDocumentName = documentNameValue;
            } else {
                setTextSourceValue(currentSourceValue);
                setDocumentNameValue(currentDocumentName);
                restoredValue = packageSourceValue;
                restoredDocumentName = undefined;
            }

            form.setFieldsValue({
                [trainingProjectExampleFormFields.sourceValue]: restoredValue,
                [trainingProjectExampleFormFields.documentName]: restoredDocumentName
            });
        }
    };

    return (
        <Modal
            className="create-edit-training-project-dialog"
            title={store.editableExample ? 'Update Example' : 'Create Example'}
            visible={store.createEditExampleDialogVisible}
            width={800}
            bodyStyle={{ minHeight: 500 }}
            onCancel={onClose}
            maskClosable={false}
            closable={false}
            destroyOnClose
            centered
            footer={[
                <Button
                    form="create-edit-training-project-form"
                    data-id="create-edit-training-project-cancel"
                    key="create-edit-training-project-cancel"
                    className="light"
                    size="large"
                    onClick={onClose}
                >
                    Cancel
                </Button>,
                <Button
                    form="create-edit-training-project-form"
                    data-id="create-training-project"
                    key="create-training-project"
                    size="large"
                    type="primary"
                    htmlType="submit"
                >
                    {store.editableExample ? 'Update' : 'Create'}
                </Button>
            ]}
        >
            <Form
                id="create-edit-training-project-form"
                layout="vertical"
                form={form}
                onFinish={onFinish}
                onValuesChange={onValuesChange}
                preserve={false}
            >
                <Form.Item
                    label="Input Source"
                    name={trainingProjectExampleFormFields.sourceType}
                    initialValue={store.editableExample?.sourceType ?? trainingProjectExampleSourceTypeValues.PackageId}
                    rules={[{ required: true, message: 'Choose input source.' }]}
                >
                    <Radio.Group>
                        <Radio.Button value={trainingProjectExampleSourceTypeValues.PackageId}>Package</Radio.Button>
                        <Radio.Button value={trainingProjectExampleSourceTypeValues.Text}>Text</Radio.Button>
                    </Radio.Group>
                </Form.Item>

                <Form.Item shouldUpdate noStyle>
                    {() => {
                        const sourceType =
                            form.getFieldValue([trainingProjectExampleFormFields.sourceType]) ??
                            trainingProjectExampleSourceTypeValues.PackageId;

                        const sourceTypeIsPackageId = sourceType === trainingProjectExampleSourceTypeValues.PackageId;

                        return (
                            <Observer>
                                {() =>
                                    sourceTypeIsPackageId ? (
                                        <Form.Item
                                            label="Package"
                                            name={trainingProjectExampleFormFields.sourceValue}
                                            initialValue={store.editableExample?.sourceValue ?? undefined}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: 'Please select package.'
                                                }
                                            ]}
                                        >
                                            <Select
                                                placeholder="Select a package"
                                                showSearch
                                                allowClear
                                                loading={store.isLoadingPackages}
                                                getPopupContainer={triggerNode =>
                                                    triggerNode.parentElement as HTMLElement
                                                }
                                                options={store.packageOptions}
                                                filterOption={(input, option) =>
                                                    (option?.label as string)
                                                        .toLowerCase()
                                                        .includes(input.toLowerCase())
                                                }
                                            />
                                        </Form.Item>
                                    ) : (
                                        <>
                                            <Form.Item
                                                label="Text"
                                                name={trainingProjectExampleFormFields.sourceValue}
                                                initialValue={store.editableExample?.sourceValue ?? undefined}
                                                rules={[{ required: true, message: 'Please paste an input example.' }]}
                                            >
                                                <Input.TextArea rows={4} placeholder="Paste input text..." allowClear />
                                            </Form.Item>
                                            <Form.Item
                                                label="Document Name"
                                                name={trainingProjectExampleFormFields.documentName}
                                                initialValue={store.editableExample?.documentName ?? undefined}
                                                rules={[
                                                    {
                                                        max: 200,
                                                        message: 'Document name must be 200 characters or less.'
                                                    }
                                                ]}
                                                style={{ marginTop: 16 }}
                                            >
                                                <Input placeholder="Document name..." allowClear maxLength={200} />
                                            </Form.Item>
                                        </>
                                    )
                                }
                            </Observer>
                        );
                    }}
                </Form.Item>

                <Form.Item
                    label="Expected Output Type"
                    name={trainingProjectExampleFormFields.outputType}
                    initialValue={store.editableExample?.outputType ?? trainingProjectExampleOutputTypeValues.Json}
                    rules={[{ required: true, message: 'Select expected output type.' }]}
                >
                    <Radio.Group>
                        <Radio.Button value={trainingProjectExampleOutputTypeValues.Json}>JSON</Radio.Button>
                        <Radio.Button value={trainingProjectExampleOutputTypeValues.Text}>Text</Radio.Button>
                    </Radio.Group>
                </Form.Item>

                <Form.Item shouldUpdate noStyle>
                    {() => {
                        const outputType =
                            form.getFieldValue([trainingProjectExampleFormFields.outputType]) ??
                            trainingProjectExampleOutputTypeValues.Json;

                        const outputTypeIsJson = outputType === trainingProjectExampleOutputTypeValues.Json;
                        const fieldLabel = outputTypeIsJson ? 'Expected Output (JSON)' : 'Expected Output (Text)';
                        const rules: Rule[] = [{ required: true, message: 'Please provide expected output.' }];

                        if (outputTypeIsJson) {
                            return (
                                <Form.Item
                                    label={fieldLabel}
                                    name={trainingProjectExampleFormFields.outputValue}
                                    initialValue={store.editableExample?.outputValue ?? undefined}
                                    rules={rules}
                                >
                                    <TrainingProjectJsonEditor
                                        fieldLabel={fieldLabel}
                                        lastSavedValue={store.editableExample?.outputValue}
                                    />
                                </Form.Item>
                            );
                        }

                        return (
                            <Form.Item
                                label={fieldLabel}
                                name={trainingProjectExampleFormFields.outputValue}
                                initialValue={store.editableExample?.outputValue ?? undefined}
                                rules={rules}
                            >
                                <Input.TextArea rows={6} placeholder="Expected output..." allowClear />
                            </Form.Item>
                        );
                    }}
                </Form.Item>

                <Form.Item
                    label="Explanation"
                    name={trainingProjectExampleFormFields.explanation}
                    initialValue={store.editableExample?.explanation ?? undefined}
                >
                    <Input.TextArea rows={4} placeholder="Enter explanation..." allowClear />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default observer(CreateEditTrainingProjectExampleModal);
